<template>
  <v-expansion-panels multiple v-if="!loading">
    <v-expansion-panel v-for="(table, tableName) in tables" :key="tableName">
      <v-expansion-panel-header>
        <v-layout row justify-start align-center>
          <div>
            <v-checkbox
              :input-value="getSelectAllValueOfGroup(table, tableName)"
              :indeterminate="table.allSelected === 'indeterminate'"
              @change="toggleAllOfGroup(table, tableName)"
              :ripple="false"
              :disabled="disabled || isViewer"
              hide-details
              color="accent"
              @click.stop=""
            />
          </div>

          <span style="font-size: 12px; color: rgba(0, 0, 0, 0.54)">
            {{ $t("permissions.types." + tableName) }}
          </span>
        </v-layout>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <v-card
          class="px-4 scroll-fire-fox"
          max-height="250"
          style="overflow: auto; box-shadow: none"
        >
          <table v-if="tableName !== permissionsAreUnique(tableName)">
            <thead>
              <tr>
                <th />
                <th
                  v-for="(actionType, actionTypeName) in table.actionTypes"
                  :key="actionTypeName"
                  class="px-2"
                >
                  <v-layout column justify-end align-center>
                    <span>
                      {{ $t("permissions.actionTypes." + actionTypeName) }}
                    </span>
                    <div>
                      <v-checkbox
                        :input-value="getSelectAllValue(actionType)"
                        :indeterminate="
                          actionType.allSelected === 'indeterminate'
                        "
                        @change="toggleAll(actionType)"
                        :ripple="false"
                        :disabled="disabled || (isViewer && !isAllowedPermissionActionType(actionTypeName))"
                        hide-details
                        color="accent"
                      />
                    </div>
                  </v-layout>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(
                  permissionSet, permissionSetName
                ) in table.permissionSets"
                :key="permissionSetName"
              >
                <th>
                  <v-layout
                    row
                    justify-space-between
                    align-center
                    class="flex-nowrap"
                  >
                    <v-flex shrink>
                      {{
                        $t("permissions.permissionSets." + permissionSetName)
                      }}
                    </v-flex>

                    <v-flex shrink>
                      <div class="ml-4">
                        <v-checkbox
                          :input-value="getSelectAllValue(permissionSet)"
                          :indeterminate="
                            permissionSet.allSelected === 'indeterminate'
                          "
                          @change="toggleAll(permissionSet)"
                          :ripple="false"
                          :disabled="disabled || isViewer"
                          hide-details
                          color="accent"
                        />
                      </div>
                    </v-flex>
                  </v-layout>
                </th>
                <td
                  v-for="(actionType, actionTypeName) in table.actionTypes"
                  :key="actionTypeName"
                >
                  <v-layout justify-center align-center>
                    <v-tooltip
                      top
                      v-if="
                        permissionSet.permissions.hasOwnProperty(actionTypeName)
                      "
                    >
                      <template v-slot:activator="{ on }">
                        <div v-on="on">
                          <v-checkbox
                            v-model="selectedPermissions"
                            :value="permissionSet.permissions[actionTypeName]"
                            :ripple="false"
                            :disabled="disabled || (isViewer && !isAllowedPermissionActionType(actionTypeName))"
                            hide-details
                            color="accent"
                          />
                        </div>
                      </template>
                      <span>
                        {{
                          $t(
                            "permissions.permissionSets." + permissionSetName) +
                          ": " + $t("permissions.actionTypes." + actionTypeName)
                        }}
                      </span>
                    </v-tooltip>

                    <span v-else style="padding-right: 15px">—</span>
                  </v-layout>
                </td>
              </tr>
            </tbody>
          </table>

          <table v-else>
            <tr
              v-for="(permissionID, permissionName) in table.permissions"
              :key="permissionName"
            >
              <th style="font-weight: normal">
                <v-layout row justify-space-between align-center>
                  <span>{{ $t(permissionName) }} </span>
                  <div class="ml-4">
                    <v-checkbox
                      v-model="selectedPermissions"
                      :value="permissionID"
                      :ripple="false"
                      :disabled="disabled || (isViewer && !['geoserver', 'object_forms'].includes(permissionName) && role !== 'modules')"
                      hide-details
                      color="accent"
                    />
                  </div>
                </v-layout>
              </th>
            </tr>
          </table>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "PermissionsList",

  props: {
    userRole: {
      type: String,
      required: false,
      default: false
    },

    role: String,

    selectedPermissionsFromParent: Array,

    disabled: Boolean,
  },

  data() {
    return {
      selectedPermissions: [],

      tables: {},

      actionTypes: {},
      permissionSets: {},

      loading: true,
    };
  },

  watch: {
    selectedPermissions(value) {
      this.$emit("update-selection", value);
    },

    selectedPermissionsFromParent(value) {
      this.selectedPermissions = value;
    },
  },

  mounted() {
    this.getTabulatedPermissions();

    this.selectedPermissions = this.selectedPermissionsFromParent;
  },

  computed: {
    /**
     * Indicates if the selected user is a Viewer
     * @returns {Boolean}
     */
    isViewer() {
      return this.userRole === 'viewer';
    }
  },

  methods: {
    ...mapActions(["setTabulatedPermissions"]),

    ////////////////////////////////
    /* Generate Permissions Tables*/
    ////////////////////////////////

    getTabulatedPermissions() {
      let tabulatedPermissions =
        this.$store.getters.getAllPermissionsTabulatedByRole(this.role);
      if (tabulatedPermissions) {
        this.tables = JSON.parse(JSON.stringify(tabulatedPermissions));
        this.loading = false;
      } else {
        this.tabulatePermissions();
      }
    },

    tabulatePermissions() {
      let allRolePermissions = this.getAllRolePermissions();
      let tables = {};
      for (let permission of allRolePermissions) {
        let actionType = permission.action_type;
        let permissionType = permission.type;
        let permissionSet = permission.permission_set;

        if (actionType === null || actionType === "") {
          this.getUniquePermission(tables, permission, permissionType);
        } else {
          this.managePermissionGroup(tables, permissionType);

          this.registerSelectablePermissionList(
            tables[permissionType].permissionSets,
            permissionSet,
            actionType,
            permission
          );

          this.registerSelectablePermissionList(
            tables[permissionType].actionTypes,
            actionType,
            permissionSet,
            permission
          );
        }
      }
      this.tables = tables;
      this.setTabulatedPermissions({
        role: this.role,
        tabulatedPermissions: tables,
      });
      this.loading = false;
    },

    getAllRolePermissions() {
      let allRolesAndPermissions =
        this.$store.getters.getAllRolesAndPermissions;
      if (allRolesAndPermissions.length > 0) {
        let role = allRolesAndPermissions.find(
          (item) => item.name === this.role
        );
        if (role) {
          return role.permissions;
        }
      }
      return [];
    },

    getUniquePermission(tables, permission, type) {
      let permissionType = this.permissionsAreUnique(type);
      if (!tables.hasOwnProperty(permissionType)) {
        tables[permissionType] = {
          allSelected: false,
          permissions: {},
        };
      }
      tables[permissionType].permissions[permission.name] = permission.id;
    },

    managePermissionGroup(tables, permissionType) {
      if (!tables.hasOwnProperty(permissionType)) {
        tables[permissionType] = {
          allSelected: false,
          actionTypes: {},
          permissionSets: {},
        };
      }
    },

    registerSelectablePermissionList(
      list,
      firstLevelName,
      secondLevelName,
      permission
    ) {
      if (!list.hasOwnProperty(firstLevelName)) {
        list[firstLevelName] = {
          allSelected: false,
          permissions: {},
        };
      }
      list[firstLevelName].permissions[secondLevelName] = permission.id;
    },

    ////////////////////////////////
    /* Determine Select All Values*/
    ////////////////////////////////

    getSelectAllValueOfGroup(table, tableName) {
      let selectAllValue;
      if (tableName === this.permissionsAreUnique(tableName)) {
        selectAllValue = this.getSelectAllValueOfUniquePermissions(table);
      } else {
        selectAllValue = this.getSelectAllValueOfTable(table);
      }
      return selectAllValue;
    },

    getSelectAllValueOfUniquePermissions(table) {
      let totalNrPermissions = Object.keys(table.permissions).length;
      let nrSelectedPermissions = this.getNrPermissionsInSetSelected(table);
      table.allSelected = this.areAllSelected(
        nrSelectedPermissions,
        totalNrPermissions
      );
      return table.allSelected;
    },

    getSelectAllValueOfTable(table) {
      let totalNrPermissionSets = Object.keys(table.permissionSets).length;

      let nrCompletePermissionSets = 0;
      let nrEmptyPermissionSets = 0;
      for (let [key, permissionSet] of Object.entries(table.permissionSets)) {
        if (permissionSet.allSelected === true) {
          nrCompletePermissionSets++;
        } else if (!permissionSet.allSelected) {
          nrEmptyPermissionSets++;
        }
      }

      let allSelected;
      if (nrCompletePermissionSets === totalNrPermissionSets) {
        allSelected = true;
      } else if (nrEmptyPermissionSets === totalNrPermissionSets) {
        allSelected = false;
      } else {
        allSelected = "indeterminate";
      }
      table.allSelected = allSelected;

      return table.allSelected;
    },

    getSelectAllValue(permissionsList) {
      let nrSetPermissions = Object.keys(permissionsList.permissions).length;
      let nrSelectedPermissions =
        this.getNrPermissionsInSetSelected(permissionsList);

      permissionsList.allSelected = this.areAllSelected(
        nrSelectedPermissions,
        nrSetPermissions
      );
      return permissionsList.allSelected;
    },

    areAllSelected(nrSelectedPermissions, totalNrPermissions) {
      let allSelected;
      if (nrSelectedPermissions <= 0) {
        allSelected = false;
      } else if (
        nrSelectedPermissions > 0 &&
        nrSelectedPermissions < totalNrPermissions
      ) {
        allSelected = "indeterminate";
      } else {
        allSelected = true;
      }
      return allSelected;
    },

    getNrPermissionsInSetSelected(permissionsList) {
      let selectedSetPermissions = [];
      for (let [key, value] of Object.entries(permissionsList.permissions)) {
        if (this.selectedPermissions.includes(value)) {
          selectedSetPermissions.push(value);
        }
      }
      return selectedSetPermissions.length;
    },

    ///////////////////////////
    /* Change Checkbox values*/
    ///////////////////////////

    toggleAllOfGroup(table, tableName) {
      if (tableName === "uniquePermissions") {
        this.toggleAll(table);
      } else {
        this.toggleAllOfTable(table);
      }
    },

    toggleAllOfTable(table) {
      if (!table.allSelected || table.allSelected === "indeterminate") {
        for (let [key, permissionSet] of Object.entries(table.permissionSets)) {
          if (!permissionSet.allSelected) {
            this.selectAll(permissionSet);
          } else if (permissionSet.allSelected === "indeterminate") {
            this.selectRemainingPermissions(permissionSet);
          }
        }
      } else {
        for (let [key, permissionSet] of Object.entries(table.permissionSets)) {
          this.deselectAll(permissionSet);
        }
      }
    },

    /**
     * Checks if the received permission is allowed for the viewer type users
     * 
     * @param {String} permissionActionType - the type of permission to be checked
     * @returns {Boolean}
     */
    isAllowedPermissionActionType(permissionActionType) {
      return ['show', 'list', 'list_all', 'list_objects'].includes(permissionActionType);
    },

    toggleAll(permissionsList) {
      if (!permissionsList.allSelected) {
        this.selectAll(permissionsList);
      } else if (permissionsList.allSelected === "indeterminate") {
        this.selectRemainingPermissions(permissionsList);
      } else {
        this.deselectAll(permissionsList);
      }
    },

    selectAll(permissionsList) {
      for (let [key, value] of Object.entries(permissionsList.permissions)) {
        this.selectedPermissions.push(value);
      }
      permissionsList.allSelected = true;
    },

    // selectAll(permissionsList) {
    //   for (let [key, value] of Object.entries(permissionsList.permissions) ) {
    //     if (this.userRole !== 'viewer' || (this.userRole === 'viewer' && key === 'show')) {
    //       this.selectedPermissions.push(value);
    //     }
    //   }
    // },

    selectRemainingPermissions(permissionsList) {
      for (let [key, value] of Object.entries(permissionsList.permissions)) {
        if (!this.selectedPermissions.includes(value)) {
          this.selectedPermissions.push(value);
        }
      }
      permissionsList.allSelected = true;
    },

    deselectAll(permissionsList) {
      for (let [key, value] of Object.entries(permissionsList.permissions)) {
        let permissionIndex = this.selectedPermissions.findIndex(
          (item) => item === value
        );
        this.selectedPermissions.splice(permissionIndex, 1);
      }
      permissionsList.allSelected = false;
    },

    /**
     * Indicates if the permissions are unique based on the value of the role of the permissions
     *
     * @param {String} type - type of permissions
     */
    permissionsAreUnique(type) {
      return this.role !== "network" ? "uniquePermissions" : type;
    },
  },
};
</script>

<style scoped>
table {
  border-collapse: collapse;
  font-size: 12px;
}

thead th,
tbody th {
  font-weight: normal;
}

thead th {
  top: 0;
  border-bottom: 1px solid black;
}

tbody th {
  left: 0;
  border-right: 1px solid black;
  text-align: left;
  min-width: 200px;
}
</style>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/Permissions/PermissionsList.vue